import { fetchProductConstraints, fetchAvailableReservations } from '@/services/varausService';
import { useState, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import store from '@/store/Store';
import { fetchAsiakkaatByKeyword } from '@/services/asiakasService';

// Asiakastiedot-lomakkeen validointi
const initialCustomerForm = {
  customerId: '',
  company: '',
  firstName: '',
  email: '',
  phoneNumber: '',
  address: '',
  postalCode: '',
  city: '',
  countryCode: 'FI',
};

const requiredFields = [];

const validateField = (field, value) => {
  if (!requiredFields.includes(field)) return null;

  if (!value || value.trim() === '') {
    return 'Tämä kenttä on pakollinen!';
  }

  if (field === 'email') {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Virheellinen sähköpostiosoite';
    }
  }

  if (field === 'phoneNumber') {
    // eslint-disable-next-line no-useless-escape
    const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    if (!phoneRegex.test(value)) {
      return 'Virheellinen puhelinnumero';
    }
  }

  return null;
};

// Päivämäärämuunnos-apufunktiot
// TODO: vähän liian sekavaa
// Extract Date object from dateObj (which might be { date: Date } or just Date)
const extractDate = (dateObj) => {
  if (!dateObj) return null;
  if (dateObj instanceof Date) return dateObj;
  if (typeof dateObj === 'object' && dateObj.date instanceof Date) return dateObj.date;
  return null;
};

// Convert Date to YYYY-MM-DD for HTML date inputs
const dateToInputFormat = (date) => {
  if (!date) return '';
  // Handle both Date and Dayjs objects
  const d = date instanceof Date ? dayjs(date) : dayjs(date);
  return d.format('YYYY-MM-DD');
};

// Convert Date to API format (DD.MM.YYYY)
const dateToApiFormat = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? dayjs(date) : dayjs(date);
  return d.format('DD.MM.YYYY');
};

// Convert Date to Varaus API format (DD-MM-YYYY)
const dateToVarausApiFormat = (date) => {
  if (!date) return null;
  const d = date instanceof Date ? dayjs(date) : dayjs(date);
  return d.format('YYYY-MM-DD');
};

// Convert YYYY-MM-DD from input to Dayjs object
const inputFormatToDate = (inputValue) => {
  if (!inputValue) return null;
  return dayjs(inputValue);
};

export const useReservationForm = ({ productId, selectedDate }) => {
  const [activeStep, setActiveStep] = useState(0);

  // Extract the Date object from selectedDate (handles { date: Date } format)
  const selectedDateObj = extractDate(selectedDate);

  // Step 1: Party size + constraints
  const [partySize, setPartySize] = useState('');
  const [minPersons, setMinPersons] = useState(1);
  const [maxPersons, setMaxPersons] = useState(10);
  const [loadingConstraints, setLoadingConstraints] = useState(false);
  const [constraintError, setConstraintError] = useState(null);

  // Step 2: Dates + availability (stored as Dayjs objects for DatePicker compatibility)
  const [arrivalDate, setArrivalDate] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);

  // Step 3: Customer choice
  const [customerChoice, setCustomerChoice] = useState('new');

  // Step 4: Customer details & search
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerForm, setCustomerForm] = useState({ ...initialCustomerForm });
  const [fieldErrors, setFieldErrors] = useState({});

  // Step 5: Confirmation
  const [totalPrice, setTotalPrice] = useState('0.00');
  const [expandedConfirmation, setExpandedConfirmation] = useState(false);

  // Step 6: After submission (success/error state)
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);

  // Set arrival date to selected date when available
  useEffect(() => {
    if (selectedDateObj) {
      setArrivalDate(dayjs(selectedDateObj));
    }
  }, [selectedDateObj]);

  // Fetch product constraints on mount
  useEffect(() => {
    if (!productId || !selectedDateObj) {
      return;
    }

    const fetchConstraints = async () => {
      setLoadingConstraints(true);
      setConstraintError(null);

      try {
        const apiDateFormat = dateToApiFormat(selectedDateObj);
        const res = await fetchProductConstraints(productId, apiDateFormat);
        setMinPersons(res.minPersons);
        setMaxPersons(res.maxPersons);
      }
      catch (error) {
        setConstraintError(error);
        console.error('Constraint fetch error:', error);
      }
      finally {
        setLoadingConstraints(false);
      }
    };

    fetchConstraints();
  }, [productId, selectedDateObj]);

  useEffect(() => {
    if (searchInput.trim().length < 3) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetchAsiakkaatByKeyword(searchInput.trim());
        setSearchResults(res);
      }
      catch (error) {
        console.error('Customer search error:', error);
        setSearchResults([]);
      }
      finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      setSearchLoading(false);
    };
  }, [searchInput]);

  // Fetch availability when party size is set
  const handleFetchAvailableReservations = useCallback(async (size) => {
    if (!productId || !selectedDateObj || !size) {
      return;
    }

    setLoadingAvailability(true);
    setAvailabilityError(null);

    try {
      const apiDateFormat = dateToApiFormat(selectedDateObj);
      const res = await fetchAvailableReservations(productId, apiDateFormat, size);
      setAvailabilityData(res.availability || []);
    }
    catch (error) {
      setAvailabilityError(error);
    }
    finally {
      setLoadingAvailability(false);
    }
  }, [productId, selectedDateObj]);

  // Reset all state
  const resetForm = useCallback(() => {
    setActiveStep(0);
    setPartySize('');
    // setArrivalDate(null);
    setDepartureDate(null);
    setAvailabilityData([]);
    setLoadingAvailability(false);
    setAvailabilityError(null);
    setCustomerChoice('new');
    setSearchInput('');
    setSearchResults([]);
    setSearchLoading(false);
    setSelectedCustomer(null);
    setCustomerForm({ ...initialCustomerForm });
    setFieldErrors({});
    setTotalPrice('0.00');
    setExpandedConfirmation(false);
    setSubmissionResult(null);
    setSubmissionError(null);
  }, []);

  // Navigation
  const handleNext = useCallback(() => {
    setActiveStep(prev => prev + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep(prev => prev - 1);
  }, []);

  // Customer form changes
  const handleCustomerFormChange = useCallback((field, value) => {
    setCustomerForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const resetCustomerForm = useCallback(() => {
    setCustomerForm(initialCustomerForm);
  }, []);

  const handleFieldBlur = useCallback((field) => {
    const error = validateField(field, customerForm[field]);
    setFieldErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  }, [customerForm]);

  const validateAllRequired = useCallback(() => {
    const errors = {};
    let isValid = true;

    requiredFields.forEach((field) => {
      const error = validateField(field, customerForm[field]);
      if (error) {
        errors[field] = error;
        isValid = false;
      }
    });

    setFieldErrors(errors);
    return isValid;
  }, [customerForm]);

  /**
   * Converts reservation data into API format, removes empty optional fields, and returns the final object ready for submission.
   */
  const prepareReservationData = useCallback(() => {
    const yritysId = store.getState().auth.yritysId;
    const reservationData = {
      companyId: yritysId,
      status: 'new',
      customer: {
        customerId: customerForm.customerId || undefined,
        firstName: customerForm.firstName || undefined,
        lastName: customerForm.lastName || undefined,
        email: customerForm.email || undefined,
        phoneNumber: customerForm.phoneNumber || undefined,
        company: customerForm.company || undefined,
        address: customerForm.address || undefined,
        postalCode: customerForm.postalCode || undefined,
        city: customerForm.city || undefined,
        countryCode: customerForm.countryCode || undefined,
      },
      products: [
        {
          identifier: yritysId + '-' + productId,
          arrivalDate: dateToVarausApiFormat(arrivalDate),
          departureDate: dateToVarausApiFormat(departureDate),
          totalPrice: parseFloat(totalPrice),
          numberOfGuests: parseInt(partySize),
        },
      ],
    };

    // Remove undefined values
    Object.keys(reservationData.customer).forEach((key) => {
      if (reservationData.customer[key] === undefined || reservationData.customer[key] === '') {
        delete reservationData.customer[key];
      }
    });

    return reservationData;
  }, [arrivalDate, departureDate, customerForm, totalPrice]);

  // Search results selection
  const handleSelectSearchResult = useCallback((customer) => {
    setSelectedCustomer(customer);
    setCustomerForm({
      customerId: customer?.customerId || '',
      company: customer?.company || '',
      firstName: customer?.firstName || '',
      lastName: customer?.lastName || '',
      email: customer?.email || '',
      phoneNumber: customer?.phone || '',
      address: customer?.address || '',
      postalCode: customer?.postalCode || '',
      city: customer?.city || '',
      countryCode: customer?.countryCode || 'FI',
    });
    setSearchResults([]);
    setSearchInput('');
  }, []);

  return {
    // Stepper
    activeStep,
    // Step 1
    partySize,
    setPartySize,
    minPersons,
    maxPersons,
    loadingConstraints,
    constraintError,
    // Step 2
    arrivalDate,
    setArrivalDate,
    departureDate,
    setDepartureDate,
    availabilityData,
    loadingAvailability,
    availabilityError,
    // Step 3
    customerChoice,
    setCustomerChoice,
    // Step 4
    searchInput,
    setSearchInput,
    searchResults,
    setSearchResults,
    searchLoading,
    setSearchLoading,
    selectedCustomer,
    setSelectedCustomer,
    customerForm,
    fieldErrors,
    // Step 5
    totalPrice,
    setTotalPrice,
    expandedConfirmation,
    setExpandedConfirmation,
    // Step 6
    submissionResult,
    setSubmissionResult,
    submissionError,
    setSubmissionError,
    // Actions
    handleNext,
    handleBack,
    setActiveStep,
    handleCustomerFormChange,
    handleSelectSearchResult,
    handleFetchAvailableReservations,
    handleFieldBlur,
    validateAllRequired,
    resetForm,
    resetCustomerForm,
    // Utility functions for converting dates
    dateToInputFormat,
    dateToApiFormat,
    dateToVarausApiFormat,
    inputFormatToDate,
    prepareReservationData,
  };
};
